import {useState} from 'react'
import {View} from 'react-native'
import {
  type InterpretedLabelValueDefinition,
  type LabelPreference,
  LABELS,
} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {useGlobalLabelStrings} from '#/lib/moderation/useGlobalLabelStrings'
import {
  usePreferencesQuery,
  usePreferencesSetContentLabelMutation,
} from '#/state/queries/preferences'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import * as ToggleButton from '#/components/forms/ToggleButton'
import {
  ChevronBottom_Stroke2_Corner0_Rounded as ChevronDown,
  ChevronTop_Stroke2_Corner0_Rounded as ChevronUp,
} from '#/components/icons/Chevron'
import {EyeSlash_Stroke2_Corner0_Rounded as EyeSlashIcon} from '#/components/icons/EyeSlash'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'
import {useAgeAssurance} from '#/ageAssurance'

const CATEGORIES: InterpretedLabelValueDefinition[] = [
  LABELS.porn,
  LABELS.sexual,
  LABELS['graphic-media'],
  LABELS.nudity,
]

/**
 * True when the quick adult-content filter fieldset is applicable: the user
 * has full age-assurance access and has already enabled adult content via
 * /moderation. Callers should gate rendering of
 * [[ContentFiltersFieldset]] (or any trigger for it) on this.
 */
export function useShowContentFiltersFieldset(): boolean {
  const {data: preferences} = usePreferencesQuery()
  const aa = useAgeAssurance()
  return !!(
    preferences &&
    aa.state.access === aa.Access.Full &&
    !aa.flags.adultContentDisabled &&
    preferences.moderationPrefs.adultContentEnabled
  )
}

/**
 * Quick Show/Warn/Hide toggle for the 4 adult-content label categories, all
 * together or expanded for individual control, plus a link to the full
 * moderation settings. Shared between the desktop sidebar card and the
 * mobile header popover - see [[useShowContentFiltersFieldset]].
 */
export function ContentFiltersFieldset() {
  const t = useTheme()
  const {_} = useLingui()
  const {data: preferences} = usePreferencesQuery()
  const globalLabelStrings = useGlobalLabelStrings()
  const {mutateAsync: setContentLabelPref} =
    usePreferencesSetContentLabelMutation()
  const [expanded, setExpanded] = useState(false)
  const [pendingAll, setPendingAll] = useState<LabelPreference | null>(null)

  if (!preferences) {
    return null
  }

  const values = CATEGORIES.map(
    def => preferences.moderationPrefs.labels[def.identifier] ?? 'warn',
  )
  const allSame = values.every(v => v === values[0])
  const commonValue = pendingAll ?? (allSame ? values[0] : undefined)

  const labelOptions = {
    hide: _(msg`Hide`),
    warn: _(msg`Warn`),
    ignore: _(msg`Show`),
  }

  const onChangeAll = async (visibility: LabelPreference) => {
    setPendingAll(visibility)
    try {
      await Promise.all(
        CATEGORIES.map(def =>
          setContentLabelPref({
            label: def.identifier,
            visibility,
            labelerDid: undefined,
          }),
        ),
      )
    } finally {
      setPendingAll(null)
    }
  }

  return (
    <View style={[a.gap_md]}>
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <EyeSlashIcon width={16} height={16} fill={t.atoms.text.color} />
        <Text style={[a.flex_1, a.text_md, a.font_semi_bold, t.atoms.text]}>
          <Trans>Content Filters</Trans>
        </Text>
        <Button
          variant="ghost"
          size="tiny"
          color="secondary"
          shape="round"
          label={
            expanded
              ? _(msg`Show combined controls`)
              : _(msg`Customize each category individually`)
          }
          onPress={() => setExpanded(!expanded)}
          style={[a.bg_transparent, {marginTop: -6, marginRight: -6}]}>
          <ButtonIcon icon={expanded ? ChevronUp : ChevronDown} size="xs" />
        </Button>
      </View>

      {expanded ? (
        <View style={[a.gap_md]}>
          {CATEGORIES.map(def => {
            const strings = globalLabelStrings[def.identifier]
            const pref =
              preferences.moderationPrefs.labels[def.identifier] ?? 'warn'
            return (
              <View key={def.identifier} style={[a.gap_xs]}>
                <Text style={[a.text_sm, a.font_semi_bold, t.atoms.text]} emoji>
                  {strings.name}
                </Text>
                <ToggleButton.Group
                  label={_(
                    msg`Configure content filtering setting for category: ${strings.name}`,
                  )}
                  values={[pref]}
                  onChange={values => {
                    void setContentLabelPref({
                      label: def.identifier,
                      visibility: values[0] as LabelPreference,
                      labelerDid: undefined,
                    })
                  }}>
                  <ToggleButton.Button
                    name="ignore"
                    label={labelOptions.ignore}>
                    <ToggleButton.ButtonText>
                      {labelOptions.ignore}
                    </ToggleButton.ButtonText>
                  </ToggleButton.Button>
                  <ToggleButton.Button name="warn" label={labelOptions.warn}>
                    <ToggleButton.ButtonText>
                      {labelOptions.warn}
                    </ToggleButton.ButtonText>
                  </ToggleButton.Button>
                  <ToggleButton.Button name="hide" label={labelOptions.hide}>
                    <ToggleButton.ButtonText>
                      {labelOptions.hide}
                    </ToggleButton.ButtonText>
                  </ToggleButton.Button>
                </ToggleButton.Group>
              </View>
            )
          })}
        </View>
      ) : (
        <View style={[a.gap_xs]}>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {allSame ? (
              <Trans>Applies to all 4 categories</Trans>
            ) : (
              <Trans>Categories set individually</Trans>
            )}
          </Text>
          <ToggleButton.Group
            label={_(
              msg`Set all content filter categories to the same setting`,
            )}
            values={commonValue ? [commonValue] : []}
            onChange={values => {
              void onChangeAll(values[0] as LabelPreference)
            }}>
            <ToggleButton.Button name="ignore" label={labelOptions.ignore}>
              <ToggleButton.ButtonText>
                {labelOptions.ignore}
              </ToggleButton.ButtonText>
            </ToggleButton.Button>
            <ToggleButton.Button name="warn" label={labelOptions.warn}>
              <ToggleButton.ButtonText>
                {labelOptions.warn}
              </ToggleButton.ButtonText>
            </ToggleButton.Button>
            <ToggleButton.Button name="hide" label={labelOptions.hide}>
              <ToggleButton.ButtonText>
                {labelOptions.hide}
              </ToggleButton.ButtonText>
            </ToggleButton.Button>
          </ToggleButton.Group>
        </View>
      )}

      <InlineLinkText
        to="/moderation"
        label={_(msg`Manage all moderation settings`)}
        style={[a.text_sm]}>
        <Trans>Manage all settings</Trans>
      </InlineLinkText>
    </View>
  )
}
